import { ActionModal, Label } from '@kcftech/design-system';
import {
    ICorporationResponse,
    ILocationResponse,
    ISystemResponse,
} from '@kcftech/prometheus-client';
import { logger } from '@sentry/react';
import { CorporationDropdown } from 'components/dropdown/CorporationDropdown';
import { LocationDropdown } from 'components/dropdown/LocationDropdown';
import { SystemDropdown } from 'pages/workbench/side_panel/workbench_tree/SystemDropdown';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { DebugValue } from 'shared/debug/DebugValue';
import { useQueryParams } from 'utils/useQueryParams';

export const QuickLinkUrlPrefix = '/quickLink';
export function QuickLinkHandler(): JSX.Element {
    const route = useLocation();
    const { push } = useHistory();
    const params = useQueryParams();
    const forceErrBtn = params.has('err');

    const routeTemplate = route.pathname.substring(QuickLinkUrlPrefix.length);

    const routeRequires = {
        corporation: route.pathname.includes(':corporationId'),
        location: route.pathname.includes(':locationId'),
        system: route.pathname.includes(':systemId'),
    };

    const [corporation, setCorporation] = useState<ICorporationResponse>();
    const [location, setLocation] = useState<ILocationResponse>();
    const [system, setSystem] = useState<ISystemResponse>();

    const allRequiredValuesProvided = !!(
        (!routeRequires.corporation || corporation) &&
        (!routeRequires.location || location) &&
        (!routeRequires.system || system)
    );

    const buildFinalLink = () => {
        let workingLink = routeTemplate;
        if (corporation) {
            workingLink = workingLink.replace(':corporationId', corporation.Id);
        }
        if (location) {
            workingLink = workingLink.replace(':locationId', location.Id);
        }
        if (system) {
            workingLink = workingLink.replace(':systemId', system.Id);
        }
        return workingLink;
    };

    const numParametersRequired = Object.values(routeRequires).reduce(
        (accum, cur) => (cur ? accum + 1 : accum),
        0,
    );

    return (
        <ActionModal
            title="Select Destination"
            actionButtonText="Go"
            actionable={allRequiredValuesProvided}
            onAction={() => {
                push(buildFinalLink());
            }}
            onClose={() => {
                push('/');
            }}
            height={200 + 100 * numParametersRequired}
        >
            <DebugValue
                description="Navigation target"
                value={{
                    routeTemplate: route.pathname.substring(QuickLinkUrlPrefix.length),
                    corporation,
                    location,
                    system,
                }}
            />

            {routeRequires.corporation && (
                <Label label="Corporation">
                    <CorporationDropdown
                        value={corporation}
                        onChange={setCorporation}
                        interactionMode="select"
                    />
                </Label>
            )}

            {routeRequires.location && (
                <Label label="Location">
                    <LocationDropdown
                        value={location}
                        onChange={setLocation}
                        corporationId={corporation?.Id}
                        interactionMode="select"
                    />
                </Label>
            )}

            {routeRequires.system && (
                <Label label="System">
                    <SystemDropdown
                        system={system}
                        onChange={setSystem}
                        corporationId={corporation?.Id}
                        locationId={location?.Id}
                        popup={{ width: undefined }}
                    />
                </Label>
            )}
            {forceErrBtn && (
                <button
                    onClick={() => {
                        logger.error('throwing intentional error');
                        throw new Error('intentional error');
                    }}
                >
                    Force an Error
                </button>
            )}
        </ActionModal>
    );
}
